import { Card, Checkbox, Collapsible, Flex, Heading, Text, VStack } from "@chakra-ui/react";
import { memo, useCallback } from "react";
import type {
  ExhibitionMercariShopsSettingFragment,
  ExhibitionRakutenSettingFragment,
  ExhibitionShopifySettingFragment,
  ExhibitionYahooShoppingSettingFragment,
} from "../graphql/generated/apollo";
import { useShopContext } from "../shop";
import { MemoizedMercariShopsExhibitionSettingsForm } from "./MercariShopsExhibitionSettingsForm";
import { PlatformCategoryTree } from "./PlatformCategoryTree";
import { RakutenExhibitionSettingsForm } from "./RakutenExhibitionSettingsForm";
import { ShopifyExhibitionSettingForm } from "./ShopifyExhibitionSettingForm";
import { Tooltip } from "./ui/tooltip";
import { YahooExhibitionSettingsForm } from "./YahooExhibitionSettingsForm";

type PlatformExhibitionSettingsFormProps = {
  platforms: { key: string; name: string }[];
  integrations: { platform: string; disabled_at?: Date }[];
  selectedPlatforms?: string[];
  setSelectedPlatforms: (newState: string[]) => void;
  baseCategories: number[];
  setBaseCategories: (newState: number[]) => void;
  storesCategories: string[];
  setStoresCategories: (newState: string[]) => void;
  mercariShopsSetting?: ExhibitionMercariShopsSettingFragment;
  setMercariShopsSetting: (newState: ExhibitionMercariShopsSettingFragment) => void;
  yahooShoppingSetting?: ExhibitionYahooShoppingSettingFragment;
  setYahooShoppingSetting: (newState: ExhibitionYahooShoppingSettingFragment) => void;
  shopifySetting?: ExhibitionShopifySettingFragment;
  setShopifySetting: (newState: ExhibitionShopifySettingFragment) => void;
  rakutenSetting?: ExhibitionRakutenSettingFragment;
  setRakutenSetting: (newState: ExhibitionRakutenSettingFragment) => void;
};

export const PlatformExhibitionSettingsForm = memo(function PlatformExhibitionSettingsForm({
  platforms,
  integrations,
  selectedPlatforms,
  setSelectedPlatforms,
  baseCategories,
  setBaseCategories,
  storesCategories,
  setStoresCategories,
  mercariShopsSetting,
  setMercariShopsSetting,
  yahooShoppingSetting,
  setYahooShoppingSetting,
  shopifySetting,
  setShopifySetting,
  rakutenSetting,
  setRakutenSetting,
}: PlatformExhibitionSettingsFormProps) {
  useShopContext();
  const cachedSetMercariShopsSetting = useCallback(setMercariShopsSetting, []);

  return (
    <VStack gap={4} alignItems="stretch">
      {platforms?.map((platform) => {
        const integration = integrations.find((integ) => integ.platform === platform.key);
        const disabled = integration && !!integration.disabled_at && new Date(integration.disabled_at) <= new Date();
        const selected = selectedPlatforms?.includes(platform.key);
        const deactivated = !selected && (!integration || disabled);

        return (
          <Collapsible.Root key={platform.key} open={selected}>
            <Flex direction="column" gap={2}>
              <Checkbox.Root
                checked={selected}
                disabled={deactivated}
                key={platform.name}
                onCheckedChange={(details) => {
                  console.log(platform.key, details);
                  const { checked } = details;
                  setSelectedPlatforms(
                    checked === true
                      ? [...(selectedPlatforms ?? []), platform.key]
                      : (selectedPlatforms?.filter((p) => p !== platform.key) ?? []),
                  );
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label>
                  {integration && !disabled ? (
                    platform.name
                  ) : (
                    <Tooltip content={`${disabled ? "再連携を行うまで" : "連携が削除されているため"}出品できません`}>
                      <Text as="del">{platform.name}</Text>
                    </Tooltip>
                  )}
                </Checkbox.Label>
              </Checkbox.Root>

              {!integration ? null : (
                <Collapsible.Content>
                  {platform.key === "BASE" || platform.key === "STORES" ? (
                    <Card.Root variant="subtle" size="sm">
                      <Card.Header>
                        <Heading size="xs" textAlign="left">
                          カテゴリ
                        </Heading>
                      </Card.Header>
                      <Card.Body>
                        <PlatformCategoryTree
                          platform={platform.key}
                          selectedCategories={
                            platform.key === "BASE"
                              ? baseCategories.map((c) => c.toString())
                              : platform.key === "STORES"
                                ? storesCategories
                                : [] /* Unexpected */
                          }
                          initialSelectedCategories={
                            platform.key === "BASE"
                              ? storesCategories.map((c) => c.toString())
                              : platform.key === "STORES"
                                ? storesCategories
                                : [] /* Unexpected */
                          }
                          onStateChanged={(categories) => {
                            switch (platform.key) {
                              case "BASE":
                                setBaseCategories(categories.map((c) => Number.parseInt(c)));
                                if (categories.length !== 0 && !selectedPlatforms?.some((pf) => pf === "BASE")) {
                                  setSelectedPlatforms([...(selectedPlatforms ?? []), "BASE"]);
                                }
                                break;
                              case "STORES":
                                setStoresCategories(categories);
                                if (categories.length !== 0 && !selectedPlatforms?.some((pf) => pf === "STORES")) {
                                  setSelectedPlatforms([...(selectedPlatforms ?? []), "STORES"]);
                                }
                                break;
                            }
                          }}
                        />
                      </Card.Body>
                    </Card.Root>
                  ) : platform.key === "MERCARI_SHOPS" ? (
                    <Card.Root variant="subtle" size="sm">
                      <MemoizedMercariShopsExhibitionSettingsForm
                        initialState={mercariShopsSetting ?? undefined}
                        onStateChanged={cachedSetMercariShopsSetting}
                      />
                    </Card.Root>
                  ) : platform.key === "YAHOO_SHOPPING" ? (
                    <Card.Root variant="subtle" size="sm">
                      <YahooExhibitionSettingsForm
                        yahooExhibitionSettings={yahooShoppingSetting}
                        onChange={(v) => {
                          setYahooShoppingSetting(v as ExhibitionYahooShoppingSettingFragment);
                        }}
                      />
                    </Card.Root>
                  ) : platform.key === "SHOPIFY" ? (
                    <Card.Root variant="subtle" size="sm">
                      <ShopifyExhibitionSettingForm
                        shopifySetting={shopifySetting}
                        setShopifySetting={setShopifySetting}
                      />
                    </Card.Root>
                  ) : platform.key === "RAKUTEN" ? (
                    <Card.Root variant="subtle" size="sm">
                      <RakutenExhibitionSettingsForm
                        rakutenExhibitionSettings={
                          rakutenSetting ?? {
                            item_type: "NORMAL",
                            genre_id: "",
                            manage_number: "",
                            shipping_method_group: undefined,
                            normal_delivery_date_id: undefined,
                            backorder_delivery_date_id: undefined,
                            inventory_display: undefined,
                            low_stock_threshold: undefined,
                            operation_normal_delivery_time_id: undefined,
                            operation_backorder_delivery_time_id: undefined,
                            ship_from_id: undefined,
                            rakuten_categories: [],
                            tagline: undefined,
                            point_multiplier_starts_at: undefined,
                            point_multiplier_ends_at: undefined,
                            point_multiplier_rate: undefined,
                          }
                        }
                        onChange={(v) => {
                          setRakutenSetting(v as ExhibitionRakutenSettingFragment);
                        }}
                      />
                    </Card.Root>
                  ) : null}
                </Collapsible.Content>
              )}
            </Flex>
          </Collapsible.Root>
        );
      })}
    </VStack>
  );
});
